import React from "react"
import styled from "styled-components"
import { useLocation } from "@reach/router"

import { Color, Spacing, Typography } from "@persona-id/design-tokens"
import Card from "@persona-id/design-system/components/Card"
import {
  ContentfulCategory,
  ContentfulTopic,
  ContentfulSubtopic,
  PageContext,
} from "../models/contentful"
import NavigationLeftAcademy from "./NavigationLeftAcademy"
import NavigationLeftArticles from "./NavigationLeftArticles"
import NavigationLeftCommunity from "./NavigationLeftCommunity"
import NavigationLeftEnterpriseEndUser from "./NavigationLeftEnterpriseEndUser"
import NavigationLeftHelp from "./NavigationLeftHelp"

const NavigationLeft = ({
  pageContext,
  currentCategory,
  currentTopic,
  currentSubtopic,
}: {
  pageContext: PageContext
  currentCategory: ContentfulCategory
  currentTopic: ContentfulTopic
  currentSubtopic: ContentfulSubtopic
}) => {
  const location = useLocation()
  const useCommunityNav =
    location.pathname.includes("community") ||
    location.pathname.includes("podcasts") ||
    location.pathname.includes("events")
  const useAcademyNav = location.pathname.includes("academy")
  // "for-end-users" is a Help Center Article category, which is why it needs to be excluded
  const useEnterpriseEndUserNav =
    location.pathname.includes("end-users") &&
    !location.pathname.includes("for-end-users")

  const renderNavigationLeftCard = () => {
    if (useCommunityNav) {
      return (
        <NavigationLeftCard>
          <NavigationLeftHeading>Community</NavigationLeftHeading>
          <NavigationLeftCommunity />
        </NavigationLeftCard>
      )
    } else if (useAcademyNav) {
      return (
        <NavigationLeftCard>
          <NavigationLeftHeading>Academy</NavigationLeftHeading>
          <NavigationLeftAcademy />
        </NavigationLeftCard>
      )
    } else if (useEnterpriseEndUserNav) {
      return (
        <NavigationLeftCard>
          <NavigationLeftEnterpriseEndUser pageContext={pageContext} />
        </NavigationLeftCard>
      )
    } else {
      return (
        <NavigationLeftCard>
          <NavigationLeftHeading>Articles</NavigationLeftHeading>
          <NavigationLeftArticles
            currentCategory={currentCategory}
            currentTopic={currentTopic}
            currentSubtopic={currentSubtopic}
          />
        </NavigationLeftCard>
      )
    }
  }

  return (
    <NavigationLeftWrapper>
      {renderNavigationLeftCard()}
      {!useEnterpriseEndUserNav && (
        <NavigationLeftCard>
          <NavigationLeftHeading>Get help now</NavigationLeftHeading>
          <NavigationLeftHelp />
        </NavigationLeftCard>
      )}
      {useCommunityNav && (
        <NavigationLeftCard>
          <NavigationLeftHeading>Join the conversation</NavigationLeftHeading>
          <NavigationLeftParagraph>
            The Persona community is an invite-only identity community where
            startup founders and early team members come together to exchange
            ideas and learn what works to build trustworthy and secure
            businesses for their customers.
          </NavigationLeftParagraph>
          <NavigationLeftParagraph>
            Fill out our{" "}
            <a
              href="https://t4q0179uk5j.typeform.com/to/t5J6hjw8"
              target="_blank"
              rel="noopener noreferrer"
            >
              request form
            </a>{" "}
            to get started.
          </NavigationLeftParagraph>
        </NavigationLeftCard>
      )}
    </NavigationLeftWrapper>
  )
}

const NavigationLeftWrapper = styled.div`
  flex-direction: column;
  column-gap: ${Spacing[4]};

  @media (max-width: 900px) {
    display: none;
  }
`

const NavigationLeftCard = styled(Card)`
  padding: ${Spacing[4]};
  width: 280px;

  a {
    color: ${Color.TextPrimary};
    text-decoration: none;
  }
`

const NavigationLeftHeading = styled("h2")`
  ${Typography.HeadingSmall};
  margin: 0px;
  margin-bottom: ${Spacing[2]};
}`

const NavigationLeftParagraph = styled.p`
  ${Typography.TextMedium};

  a {
    text-decoration: underline;
  }

  :last-of-type {
    margin-bottom: 0px;
  }
`

export default NavigationLeft
